import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import PasswordResetPage from './pages/PasswordResetPage';
import AccountInfoPage from './pages/AccountInfoPage';
import AdminPage from './pages/AdminPage';
import CoachDashboardPage from './pages/CoachDashboardPage';
import PlayerDashboardPage from './pages/PlayerDashboardPage';
import ParentDashboardPage from './pages/ParentDashboardPage';
import AdminRoute from './components/AdminRoute';
import CoachRoute from './components/CoachRoute';
import PlayerRoute from './components/PlayerRoute';
import ParentRoute from './components/ParentRoute';

// Placeholder components - to be implemented
const NotFoundPage = () => (
  <div style={{ padding: '20px' }}>
    <h1>404 - Page Not Found</h1>
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route wrapper (redirects to default dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (user && profile) {
    const getDefaultForRole = (role) => {
      switch (role) {
        case 'player':
          return 'player';
        case 'parent':
          return 'parent';
        case 'coach':
          return 'coach';
        case 'admin':
        case 'super_admin':
          return 'admin';
        default:
          return 'player';
      }
    };
    const defaultDash = profile.default_dashboard || getDefaultForRole(profile.role);
    return <Navigate to={`/${defaultDash}`} replace />;
  }

  return children;
};

// Default redirect component (redirects to user's preferred or role-based dashboard)
const DefaultRedirect = () => {
  const { profile, loading } = useAuth();

  const getDefaultForRole = (role) => {
    switch (role) {
      case 'player':
        return 'player';
      case 'parent':
        return 'parent';
      case 'coach':
        return 'coach';
      case 'admin':
      case 'super_admin':
        return 'admin';
      default:
        return 'player';
    }
  };

  // Wait for profile to load before redirecting
  if (loading || !profile) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  const defaultDash = profile.default_dashboard || getDefaultForRole(profile.role);
  return <Navigate to={`/${defaultDash}`} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountInfoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/coach"
        element={
          <CoachRoute>
            <CoachDashboardPage />
          </CoachRoute>
        }
      />
      <Route
        path="/player"
        element={
          <PlayerRoute>
            <PlayerDashboardPage />
          </PlayerRoute>
        }
      />
      <Route
        path="/parent"
        element={
          <ParentRoute>
            <ParentDashboardPage />
          </ParentRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DefaultRedirect />
          </ProtectedRoute>
        }
      />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
